import { LocaleSegmentRegex } from '@/enums/parse'
import type { CountryCodes, Locales } from '@/enums/general'
import type { RouteLocationNormalized } from 'vue-router'
import { logError } from '@/utils/general'
import { getClientIp } from '@/utils/ip'

export const parseLanguage = (language: string) => {
  if (!language) return ''
  const value = language.split('-')[0]
  return value ? value.toLowerCase() : value
}

export const parseCountry = (language: string) => {
  if (!language) return ''
  const value = language.split('-')[1]
  return value ? value.toLowerCase() : value
}

type LocaleCountry = {
  locale: string
  country: string
}

export const parseLocaleCountryFromPath = (path: string): LocaleCountry | null => {
  const match = path.match(LocaleSegmentRegex)
  if (!match) {
    return null
  }

  return {
    locale: match[1]?.toLowerCase() || '',
    country: match[2]?.toLowerCase() || ''
  }
}

interface IsValidLocaleCountryParamParams {
  appStore: ReturnType<typeof useAppStore>
  localeCountryParam: string | undefined
}

/**
 * Checks if the locale-country param is valid
 *
 * @param params - Object containing appStore and localeCountryParam
 * @returns boolean indicating if the locale-country param is valid
 */
export function isValidLocaleCountryParam({ appStore, localeCountryParam }: IsValidLocaleCountryParamParams): boolean {
  if (!localeCountryParam) return false

  const locale = localeCountryParam.split('-')[0]
  const country = localeCountryParam.split('-')[1]

  return (
    appStore.locales.includes(locale as (typeof Locales)[keyof typeof Locales]) &&
    appStore.countries.includes(country as (typeof CountryCodes)[keyof typeof CountryCodes])
  )
}

interface LocaleCountryData {
  locale: string
  country: string
}

interface DetermineLocaleCountryPrefixParams {
  route: RouteLocationNormalized | { path: string; fullPath: string }
  appStore: ReturnType<typeof useAppStore>
  authStore: ReturnType<typeof useAuthStore>
  cookieLocale?: string | null
  cookieCountry?: string | null
  acceptLanguageHeader?: string
  requestIp?: string
}

interface GetBrowserValuesParams {
  appStore: ReturnType<typeof useAppStore>
  defaults: LocaleCountryData
  browserLocales: readonly string[] | string[]
  requestIp?: string
}

/**
 * Checks if only the country param is filled (e.g., /fr, /gb)
 *
 * @param params - Object containing appStore and localeCountryParam
 * @returns boolean indicating if only valid country param is present
 */
export function hasOnlyValidCountryParam({
  appStore,
  localeCountryParam
}: {
  appStore: ReturnType<typeof useAppStore>
  localeCountryParam: string | undefined
}): boolean {
  const onlyContainsCountryParam = !localeCountryParam?.includes('-')
  const country = localeCountryParam || ''

  return onlyContainsCountryParam && appStore.countries.includes(country as (typeof CountryCodes)[keyof typeof CountryCodes])
}

/**
 * Detects locale and country from browser values (Accept-Language header and IP geolocation)
 *
 * @param params - Object containing appStore, defaults, browserLocales, and optional requestIp
 * @returns LocaleCountryData object with detected locale and country
 */
async function getBrowserValues({
  appStore,
  defaults,
  browserLocales,
  requestIp
}: GetBrowserValuesParams): Promise<LocaleCountryData> {
  let locale = defaults.locale
  let country = defaults.country

  // Get browser language
  browserLocales.some((value) => {
    const newLocale = value.split('-')[0]

    if (!appStore.locales.includes(newLocale as (typeof Locales)[keyof typeof Locales])) {
      return false
    }

    locale = newLocale || ''
    return true
  })

  // Get country based on ip
  if (import.meta.server && requestIp) {
    try {
      const geoIpModule = await import('geoip-lite')
      const geoIp = geoIpModule.default || geoIpModule
      const geo = geoIp.lookup(requestIp)

      if (geo) {
        const geoCountry = geo.country.toLowerCase()

        if (appStore.countries.includes(geoCountry as (typeof CountryCodes)[keyof typeof CountryCodes])) {
          country = geoCountry
        }
      }
    } catch (exception) {
      logError(exception)
    }
  }

  if (!import.meta.server) {
    try {
      const response = await fetch('https://get.geojs.io/v1/ip/country.json')
      const { country: geoCountry } = (await response.json()) as { country: string }

      const lowerCaseGeoCountry = geoCountry.toLowerCase()

      if (appStore.countries.includes(lowerCaseGeoCountry as (typeof CountryCodes)[keyof typeof CountryCodes])) {
        country = lowerCaseGeoCountry
      }
    } catch (e) {
      logError(e)
    }
  }

  return {
    locale,
    country
  }
}

/**
 * Gets the locale and country using browser detection
 *
 * @param params - Object containing appStore, authStore, defaults, browserLocales, and optional requestIp
 * @returns LocaleCountryData object with locale and country
 */
async function getData({
  appStore,
  authStore,
  defaults,
  browserLocales,
  requestIp
}: {
  appStore: ReturnType<typeof useAppStore>
  authStore: ReturnType<typeof useAuthStore>
  defaults: LocaleCountryData
  browserLocales: readonly string[] | string[]
  requestIp?: string
}): Promise<LocaleCountryData> {
  const data: LocaleCountryData = { ...defaults }
  let browserValues: LocaleCountryData = { ...defaults }

  browserValues = await getBrowserValues({ appStore, defaults, browserLocales, requestIp })
  data.locale = browserValues.locale
  data.country = browserValues.country

  // Override country for authenticated users
  if (authStore.isAuthenticated) {
    data.country = authStore.user?.user?.country?.name?.toLowerCase() || defaults.country
  }

  return data
}

/**
 * Determines the locale-country prefix that should be used for a given route.
 * This function implements the same logic as the i18n middleware but returns
 * the prefix string instead of performing redirects.
 *
 * @param params - Object containing route, stores, cookies, and optional headers
 * @returns Promise resolving to the locale-country prefix string (e.g., "nl-nl")
 *
 * @example
 * ```ts
 * import { determineLocaleCountryPrefix } from '@/utils/parse'
 * import { CLOCALE, CCOUNTRY } from '@/utils/cookie/cookieTypes'
 *
 * const prefix = await determineLocaleCountryPrefix({
 *   route: useRoute(),
 *   appStore: useAppStore(),
 *   authStore: useAuthStore(),
 *   cookieLocale: useCookie(CLOCALE).value,
 *   cookieCountry: useCookie(CCOUNTRY).value
 * })
 * // Returns: "nl-nl"
 * ```
 */
export async function determineLocaleCountryPrefix({
  route,
  appStore,
  authStore,
  cookieLocale,
  cookieCountry,
  acceptLanguageHeader,
  requestIp
}: DetermineLocaleCountryPrefixParams): Promise<string> {
  // Step 1: Extract and prepare initial data
  const pathSegments = route.path.split('/').filter(Boolean)
  const localeCountryParam = pathSegments[0] // First segment should be locale-country

  // Build defaults object
  const defaults: LocaleCountryData = {
    locale: (cookieLocale as string) || 'en',
    country: authStore.loggedIn
      ? authStore.user?.user?.country?.name?.toLowerCase() ?? appStore.defaultCountry
      : (cookieCountry as string) || appStore.defaultCountry
  }

  // Step 2: Handle no prefix case (root or path without locale-country)
  if (!localeCountryParam) {
    if (cookieLocale && cookieCountry) {
      // We have cookies - return cookie-based locale-country
      return `${cookieLocale}-${cookieCountry}`
    } else {
      // No cookies - determine locale/country using browser detection
      let browserLocales: readonly string[] | string[] = []

      if (import.meta.server) {
        // Server-side: use accept-language header if provided, otherwise default
        const headerValue = acceptLanguageHeader ?? 'nl,en-US;q=0.7,en;q=0.3'
        browserLocales = String(headerValue).split(';')[0]?.split(',') || []

        // Get IP address - prefer provided requestIp, fallback to authStore
        let ip: string | undefined = requestIp || authStore.ipAddress
        if (!ip && import.meta.server) {
          try {
            const event = useRequestEvent()
            if (event) {
              ip = getClientIp(event) || undefined
            }
          } catch (e) {
            logError(e)
          }
        }

        const { locale, country } = await getData({ appStore, authStore, defaults, browserLocales, requestIp: ip })
        return `${locale}-${country}`
      } else if (import.meta.client && navigator.languages) {
        // Client-side: use navigator.languages
        browserLocales = navigator.languages
        const { locale, country } = await getData({ appStore, authStore, defaults, browserLocales })
        return `${locale}-${country}`
      } else {
        // Fallback to defaults
        return `${defaults.locale}-${defaults.country}`
      }
    }
  }

  // Step 3: Validate locale-country parameter
  if (!isValidLocaleCountryParam({ appStore, localeCountryParam })) {
    // Step 4: Handle invalid locale-country parameter
    // Special case - only country provided (e.g., /fr, /gb)
    if (hasOnlyValidCountryParam({ appStore, localeCountryParam })) {
      // Return cookieLocale-country (or default locale if no cookie)
      const locale = (cookieLocale as string) || defaults.locale
      return `${locale}-${localeCountryParam}`
    }

    // Otherwise, completely invalid - return cookie-based locale-country
    if (cookieLocale && cookieCountry) {
      return `${cookieLocale}-${cookieCountry}`
    }
    // Fallback to defaults if no cookies
    return `${defaults.locale}-${defaults.country}`
  }

  // Step 5: Handle authenticated user country override
  // Valid locale-country URL - extract locale and country
  const [locale, country] = String(localeCountryParam).split('-')

  if (authStore.isAuthenticated && country !== authStore.user?.user?.country?.name?.toLowerCase()) {
    // User's country doesn't match URL - return locale-userCountry
    const userCountry = authStore.user?.user?.country?.name?.toLowerCase() || defaults.country
    return `${locale}-${userCountry}`
  }

  // Step 6: Return the valid locale-country prefix
  return `${locale || defaults.locale}-${country || defaults.country}`
}
